import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {get, ref} from 'firebase/database';
import {EmailAuthProvider, reauthenticateWithCredential, updatePassword,} from 'firebase/auth';
import {database} from '../../config/firebase';
import {useAuth} from '../../hooks/useAuth';

const Profile = () => {
    const navigate = useNavigate();
    const {currentUser, userRole, logout} = useAuth();

    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [roleName, setRoleName] = useState('');
    const [church, setChurch] = useState(null);

    // États pour le changement de mot de passe
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState(null);
    const [setPasswordSuccess] = useState(false);

    // État pour la confirmation de déconnexion
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    // Charger les données de l'utilisateur
    useEffect(() => {
        const fetchUserData = async () => {
            if (!currentUser) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                // Récupérer le profil de l'utilisateur
                const userRef = ref(database, `users/${currentUser.uid}/profile`);
                const userSnapshot = await get(userRef);

                if (userSnapshot.exists()) {
                    setUserData(userSnapshot.val());

                    // Si l'utilisateur a une église, récupérer les détails
                    if (userRole?.role === 'church_admin' && userRole?.churchId) {
                        const churchRef = ref(database, `churches/${userRole.churchId}`);
                        const churchSnapshot = await get(churchRef);

                        if (churchSnapshot.exists()) {
                            setChurch({
                                id: userRole.churchId,
                                ...churchSnapshot.val()
                            });
                        }
                    }
                }

                // Définir le nom du rôle pour l'affichage
                switch (userRole?.role) {
                    case 'admin':
                        setRoleName('Administrateur général');
                        break;
                    case 'church_admin':
                        setRoleName('Administrateur d\'église');
                        break;
                    default:
                        setRoleName('Utilisateur');
                }
            } catch (error) {
                console.error("Erreur lors du chargement des données de l'utilisateur:", error);
                setError("Impossible de charger vos informations de profil. Veuillez réessayer plus tard.");
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [currentUser, userRole]);

    // Gérer les changements dans le formulaire de mot de passe
    const handlePasswordChange = (e) => {
        const {name, value} = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Réinitialiser le formulaire de mot de passe
    const resetPasswordForm = () => {
        setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setPasswordError(null);
        setPasswordSuccess(false);
    };

    // Soumettre le changement de mot de passe
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPasswordLoading(true);
        setPasswordError(null);
        setPasswordSuccess(false);

        try {
            // Vérifier que les mots de passe correspondent
            if (passwordData.newPassword !== passwordData.confirmPassword) {
                throw new Error("Les nouveaux mots de passe ne correspondent pas");
            }

            // Vérifier la longueur du mot de passe
            if (passwordData.newPassword.length < 6) {
                throw new Error("Le nouveau mot de passe doit contenir au moins 6 caractères");
            }

            // Réauthentifier l'utilisateur
            const credential = EmailAuthProvider.credential(
                currentUser.email,
                passwordData.currentPassword
            );

            await reauthenticateWithCredential(currentUser, credential);

            // Mettre à jour le mot de passe
            await updatePassword(currentUser, passwordData.newPassword);

            // Réinitialiser le formulaire et afficher le succès
            resetPasswordForm();
            setPasswordSuccess(true);
            setShowPasswordForm(false);
            setSuccess("Votre mot de passe a été mis à jour avec succès.");
        } catch (error) {
            console.error("Erreur lors du changement de mot de passe:", error);

            // Messages d'erreur personnalisés
            if (error.code === 'auth/wrong-password') {
                setPasswordError("Le mot de passe actuel est incorrect");
            } else if (error.code === 'auth/too-many-requests') {
                setPasswordError("Trop de tentatives. Veuillez réessayer plus tard");
            } else {
                setPasswordError(error.message);
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    // Gérer la déconnexion
    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error("Erreur lors de la déconnexion:", error);
            setError("Une erreur est survenue lors de la déconnexion. Veuillez réessayer.");
        }
    };

    // Formater la date pour l'affichage
    const formatDate = (timestamp) => {
        if (!timestamp) return 'Date inconnue';
        const date = new Date(timestamp);
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
                <div className="flex items-center">
                    <Link to="/dashboard"
                          className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
                        <svg className="-ml-1 mr-1 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd"
                                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                  clipRule="evenodd"/>
                        </svg>
                        Tableau de bord
                    </Link>
                </div>
                <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center">
                        <div
                            className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center text-2xl font-bold text-indigo-700">
                            {userData?.prenom?.charAt(0) || ''}
                            {userData?.nom?.charAt(0) || ''}
                        </div>
                        <div className="ml-4">
                            <h1 className="text-2xl font-bold text-gray-900">Mon Profil</h1>
                            <p className="text-sm text-gray-500">{roleName}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowLogoutConfirm(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                        <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                             fill="currentColor">
                            <path fillRule="evenodd"
                                  d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm11 2h-1v7.586l2.293-2.293 1.414 1.414-4 4a1 1 0 01-1.414 0l-4-4 1.414-1.414L11 12.586V5h3z"/>
                        </svg>
                        Se déconnecter
                    </button>
                </div>
            </div>

            {/* Affichage des messages */}
            {error && (
                <div className="mb-6 bg-red-50 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                    <p>{error}</p>
                </div>
            )}

            {success && (
                <div className="mb-6 bg-green-50 border-l-4 border-green-500 text-green-700 p-4" role="alert">
                    <p>{success}</p>
                </div>
            )}

            {/* Informations de profil */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                <div className="px-4 py-5 sm:px-6 bg-gray-50">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Informations personnelles
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                        Détails de votre compte administrateur.
                    </p>
                </div>
                <div className="border-t border-gray-200">
                    <dl>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Nom complet</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {userData?.prenom} {userData?.nom}
                            </dd>
                        </div>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Adresse email</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {currentUser?.email}
                            </dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Type de compte</dt>
                            <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                <span
                    className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800">
                  {roleName}
                </span>
                            </dd>
                        </div>
                        {userRole?.role === 'church_admin' && church && (
                            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Église administrée</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    <Link to={`/churches/${church.id}`}
                                          className="text-indigo-600 hover:text-indigo-900">
                                        {church.nom}
                                    </Link>
                                </dd>
                            </div>
                        )}
                        {userData?.createdAt && (
                            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Date de création du compte</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {formatDate(userData.createdAt)}
                                </dd>
                            </div>
                        )}
                    </dl>
                </div>
            </div>

            {/* Sécurité */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6 bg-gray-50">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Sécurité du compte
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                        Options pour gérer la sécurité de votre compte.
                    </p>
                </div>
                <div className="border-t border-gray-200 px-4 py-5">
                    {showPasswordForm ? (
                        <form onSubmit={handlePasswordSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                                    Mot de passe actuel
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="currentPassword"
                                        name="currentPassword"
                                        type="password"
                                        required
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordChange}
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                                    Nouveau mot de passe
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="newPassword"
                                        name="newPassword"
                                        type="password"
                                        required
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordChange}
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    Minimum 6 caractères
                                </p>
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                    Confirmer le nouveau mot de passe
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type="password"
                                        required
                                        value={passwordData.confirmPassword}
                                        onChange={handlePasswordChange}
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>

                            {passwordError && (
                                <div className="text-sm text-red-600" role="alert">
                                    {passwordError}
                                </div>
                            )}

                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        resetPasswordForm();
                                        setShowPasswordForm(false);
                                    }}
                                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={passwordLoading}
                                    className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${passwordLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {passwordLoading ? (
                                        <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg"
                           fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Enregistrement...
                    </span>
                                    ) : 'Changer le mot de passe'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <button
                            onClick={() => setShowPasswordForm(true)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg"
                                 viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd"
                                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                      clipRule="evenodd"/>
                            </svg>
                            Changer mon mot de passe
                        </button>
                    )}
                </div>
            </div>

            {/* Modal de confirmation de déconnexion */}
            {showLogoutConfirm && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog"
                     aria-modal="true">
                    <div
                        className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                             aria-hidden="true"></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen"
                              aria-hidden="true">&#8203;</span>
                        <div
                            className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div
                                        className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg"
                                             fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                                        </svg>
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                            Confirmer la déconnexion
                                        </h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Êtes-vous sûr de vouloir vous déconnecter ? Vous devrez vous reconnecter
                                                pour accéder à votre compte.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleLogout}
                                >
                                    Se déconnecter
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setShowLogoutConfirm(false)}
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;
